// Ch78 · the living profile (GR-16 D2 / GR-17): field-by-field facts with
// provenance and decay. Read + two append-only writes. Mirrors
// backend/api/routes/client_profile.py 1:1.
import { api } from "../lib/api";

export type ProfileFieldKey =
  | "budget"
  | "move_in"
  | "bedrooms"
  | "neighborhoods"
  | "pets"
  | "commute"
  | "must_haves";

export type ProfileFieldState = "known" | "stale" | "unknown" | "cleared";

export interface ProfileField {
  field: ProfileFieldKey;
  label: string;
  value: unknown;
  fact_id?: string | null;
  created_by?: string | null;
  cleared?: boolean;
  state: ProfileFieldState;
  source: string | null;
  /** "the website form" · "a text message" · "typed by a locator" … */
  source_label: string | null;
  source_ref: string | null;
  observed_at: string | null;
  age_days: number | null;
  half_life_days: number;
  confidence: number | null;
  weight: number;
  /** The question to ask when the field is unknown or stale. */
  ask: string;
}

export interface ClientProfile {
  client_id: string;
  fields: ProfileField[];
  understanding: {
    score: number; // 0..1
    label: string;
    known: number;
    stale: number;
    unknown: number;
    cleared?: number;
    total: number;
  };
  ask_next: { field: ProfileFieldKey; label: string; question: string; state: "unknown" | "stale" } | null;
  computed_at: string;
  /** Set when the 095 ledger could not be read (not applied yet). */
  facts_error: string | null;
  history_count: number;
}

export async function fetchClientProfile(clientId: string): Promise<ClientProfile> {
  return api.get<ClientProfile>(`/clients/${encodeURIComponent(clientId)}/profile`);
}

export async function setClientProfileFact(
  clientId: string,
  field: ProfileFieldKey,
  value: unknown,
  note?: string,
): Promise<ClientProfile> {
  return api.post<ClientProfile>(`/clients/${encodeURIComponent(clientId)}/profile/facts`, { field, value, note });
}

export async function clearClientProfileFact(clientId: string, field: ProfileFieldKey): Promise<ClientProfile> {
  const profile = await api.post<ClientProfile>(`/clients/${encodeURIComponent(clientId)}/profile/clear`, { field, confirmed: true });
  const cleared = profile.fields.find((f) => f.field === field);
  if (profile.client_id !== clientId || profile.facts_error || cleared?.state !== "cleared" || !cleared.fact_id) {
    throw new Error("Clear outcome could not be verified. Reload the profile before retrying.");
  }
  return profile;
}

export async function confirmClientProfileFact(clientId: string, field: ProfileFieldKey): Promise<ClientProfile> {
  return api.post<ClientProfile>(`/clients/${encodeURIComponent(clientId)}/profile/confirm`, { field });
}

/** Render any fact value as text. Lists join; numbers on the budget field
 * read as money; nothing → "Unknown". */
export function formatProfileValue(field: ProfileFieldKey, value: unknown): string {
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
    return "Unknown";
  }
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (field === "budget" && typeof value === "number") {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
  }
  return String(value);
}

/** Parse what a locator typed into the value the ledger stores. */
export function parseProfileInput(field: ProfileFieldKey, text: string): unknown {
  const t = text.trim();
  if (!t) return null;
  if (field === "budget") {
    const n = Number(t.replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (field === "bedrooms") {
    if (t === "0" || t.toLowerCase() === "studio") return 0;
    const n = Number(t.replace(/[^0-9]/g, ""));
    return Number.isFinite(n) && n > 0 ? n : t;
  }
  if (field === "neighborhoods" || field === "must_haves") {
    const parts = t.split(",").map((s) => s.trim()).filter(Boolean);
    return parts.length ? parts : null;
  }
  return t;
}
